import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlane,
  faPhone,
  faUser,
  faSuitcase,
  faChevronRight,
  faCircleInfo,
  faShieldHalved,
  faCircleCheck,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";
import { mockFlights } from "../data/mockFlights";
import { airlines } from "../data/airlines";
import { config } from "../config";
import { useNavigate } from "react-router-dom";
import ProgressBar from "../components/ProgressBar";

function BookingSummary() {
  const navigate = useNavigate();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [contactDetails, setContactDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});
  const flight = mockFlights[1];
  const airline = airlines[flight.operatorCode];

  const handleContactChange = (field, value) => {
    setContactDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    // Basic phone validation - can be made more specific based on requirements
    const re = /^[+]?[\d\s-]{10,}$/;
    return re.test(phone);
  };

  const handleContinue = async () => {
    const newErrors = {};
    if (!contactDetails.firstName)
      newErrors.firstName = "First name is required";
    if (!contactDetails.lastName) newErrors.lastName = "Last name is required";
    if (!contactDetails.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(contactDetails.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!contactDetails.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhone(contactDetails.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        const response = await fetch(
          `${config.backendServer}/api/booking-confirmation`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              bookingRef: "AMT123456",
              email: contactDetails.email,
              customerName: `${contactDetails.firstName} ${contactDetails.lastName}`,
              flightDetails: {
                origin: flight.origin,
                destination: flight.destination,
                outbound_date: flight.outbound_date,
                outbound_time: flight.outbound_time,
                return_date: flight.return_date,
                return_time: flight.return_time,
              },
              payment: {
                total: flight.price,
                method: selectedPaymentMethod || "Not specified",
                date: new Date().toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }),
              },
            }),
          }
        );

        const data = await response.json();

        if (!data.success) {
          console.error("Failed to send confirmation email:", data.message);
        }

        navigate("/booking/confirmation");
      } catch (error) {
        console.error("Error sending confirmation email:", error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container pb-4 mx-auto px-4 md:px-40 lg:px-40">
        <div className="flex justify-between py-6 items-center border-b">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {flight.origin} <FontAwesomeIcon icon={faPlane} size="sm" />{" "}
              {flight.destination}
            </h1>
            <p className="text-gray-600">Fri, 14 Feb '25 • Sun, 16 Feb '25</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">£{flight.price}.00</p>
            <p className="text-gray-600">Total price</p>
          </div>
        </div>
        <ProgressBar currentStep={2} />

        {/* Flight Summary */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={faPlane}
                className={`text-${config.primaryColor}`}
              />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Your flights</h2>
            </div>
          </div>

          {/* Flight Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-white rounded-lg p-1.5 flex items-center justify-center border border-gray-100">
                <img
                  src={airline.logo}
                  alt={airline.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <p>
                  {airline.name} • {flight.duration} • Direct
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>LHR {flight.outbound_time}</span>
                  <FontAwesomeIcon icon={faChevronRight} size="xs" />
                  <span>CDG {flight.outbound_arrival_time}</span>
                </div>
              </div>
            </div>

            {flight.return_date && (
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white rounded-lg p-1.5 flex items-center justify-center border border-gray-100">
                  <img
                    src={airline.logo}
                    alt={airline.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <p>
                    {airline.name} • {flight.return_duration} • Direct
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>CDG {flight.return_time}</span>
                    <FontAwesomeIcon icon={faChevronRight} size="xs" />
                    <span>LHR {flight.return_arrival_time}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Details */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={faPhone}
                className={`text-${config.primaryColor}`}
              />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Contact details</h2>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={contactDetails.firstName}
                  onChange={(e) =>
                    handleContactChange("firstName", e.target.value)
                  }
                  className={`w-full p-2 border ${
                    errors.firstName ? "border-red-500" : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500`}
                />
                {errors.firstName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.firstName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={contactDetails.lastName}
                  onChange={(e) =>
                    handleContactChange("lastName", e.target.value)
                  }
                  className={`w-full p-2 border ${
                    errors.lastName ? "border-red-500" : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500`}
                />
                {errors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={contactDetails.email}
                onChange={(e) => handleContactChange("email", e.target.value)}
                className={`w-full p-2 border ${
                  errors.email ? "border-red-500" : "border-gray-300"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={contactDetails.phone}
                onChange={(e) => handleContactChange("phone", e.target.value)}
                className={`w-full p-2 border ${
                  errors.phone ? "border-red-500" : "border-gray-300"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500`}
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
            </div>
          </div>
        </div>

        {/* Passengers */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={faUser}
                className={`text-${config.primaryColor}`}
              />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Passengers</h2>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="bg-coral-100 rounded-full w-12 h-12 flex items-center justify-center text-xl font-semibold text-coral-600">
                J
              </div>
              <div>
                <p className="font-semibold">Josh</p>
                <p className="text-gray-600">joshsparkes6@gmail.com</p>
                <p className="text-gray-600">+44 749329167</p>
              </div>
              <FontAwesomeIcon
                icon={faChevronRight}
                className="ml-auto text-gray-400"
              />
            </div>
          </div>
        </div>

        {/* Ancillaries */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={faSuitcase}
                className={`text-${config.primaryColor}`}
              />
            </div>
            <h2 className="text-lg font-semibold">Ancillaries</h2>
          </div>

          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faSuitcase}
                    className="text-gray-600"
                  />
                </div>
                <span>Seats</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Not added</span>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="text-gray-400"
                />
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faSuitcase}
                    className="text-gray-600"
                  />
                </div>
                <span>Extra baggage</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Not added</span>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="text-gray-400"
                />
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faShieldHalved}
                    className="text-gray-600"
                  />
                </div>
                <span>Baggage protection</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Not added</span>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="text-gray-400"
                />
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faShieldHalved}
                    className="text-gray-600"
                  />
                </div>
                <span>Cancellation protection</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Not added</span>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="text-gray-400"
                />
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    className="text-gray-600"
                  />
                </div>
                <span>Automatic check-in</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Not added</span>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="text-gray-400"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Payment Selection */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faBolt}
                  className={`text-${config.primaryColor}`}
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Payment selection</h2>
              </div>
            </div>
          </div>

          <div className="border border-purple-200 rounded-lg overflow-hidden">
            <div className="p-4 bg-gray-50">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faBolt}
                  className={`text-${config.primaryColor}`}
                />
                <span className="font-semibold">Popular</span>
              </div>
            </div>

            <div className="divide-y">
              {[
                {
                  name: "Affirm",
                  description:
                    "From short-term, 0% APR to 24-month instalments",
                  logo: "/affirm-logo.png",
                },
                {
                  name: "Clearpay",
                  description: "Pay in 4 installments",
                  logo: "/clearpay-logo.png",
                },
                {
                  name: "PayPal & PayPal Credit",
                  description: "PayPal Credit – Buy now, pay over time",
                  logo: "/paypal-logo.png",
                },
                {
                  name: "Klarna",
                  description: "Pay in 3 installments",
                  logo: "/klarna-logo.png",
                },
              ].map((method, index) => (
                <label
                  key={index}
                  className="flex items-center p-4 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="radio"
                    name="payment"
                    className="form-radio text-purple-600"
                    checked={selectedPaymentMethod === method.name}
                    onChange={() => setSelectedPaymentMethod(method.name)}
                  />
                  <div className="ml-4">
                    <p className="font-semibold">{method.name}</p>
                    <p className="text-gray-600 text-sm">
                      {method.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Price Breakdown */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={faCircleInfo}
                className={`text-${config.primaryColor}`}
              />
            </div>
            <h2 className="text-lg font-semibold">Price breakdown</h2>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between">
              <span>Flights</span>
              <span>£{flight.price}.00</span>
            </div>
            <div className="flex justify-between font-bold text-lg">
              <span>Total</span>
              <span>£{flight.price}.00</span>
            </div>
          </div>
        </div>

        <button
          onClick={handleContinue}
          className={`w-full bg-${config.primaryColor} text-white py-4 rounded-full hover:bg-purple-700 transition-colors text-lg font-semibold`}
        >
          Continue to payment
        </button>
      </div>
    </div>
  );
}

export default BookingSummary;
